export const PERMISSAO_OPTIONS = [
  { value: '', desc: 'Sem permissão' },
  { value: 'r', desc: 'Somente leitura' },
  { value: 'w', desc: 'Leitura e edição' },
];

export const PERMISSOES_2FA_GROUPS = [
  {
    id: 'configurador',
    title: 'Configurador',
    icon: 'fas fa-cog',
    items: [
      {
        label: 'Configurador - Gerenciar usuários',
        description:
          'Autoriza convidar, editar e excluir usuários do Configurador.',
      },
      {
        label: 'Configurador - Alterar configurações críticas',
        description: 'Permite alterar parâmetros sensíveis do sistema.',
      },
      {
        label: 'Apps OAuth - Gerenciar aplicações',
        description: 'Autoriza criar, editar e excluir apps OAuth/OIDC.',
      },
    ],
  },
  {
    id: 'polgo',
    title: 'Polgo',
    icon: 'fas fa-gift',
    items: [
      {
        label: 'Polgo - Entregar prêmio',
        description: 'Autoriza entrega manual de prêmios.',
      },
      {
        label: 'Polgo - Entregar prêmio de cupons sorteados',
        description: 'Autoriza entrega de prêmios vinculados a cupons sorteados.',
      },
      {
        label: 'Polgo - Alterar tabela premiação',
        description: 'Permite editar tabelas de premiação.',
      },
      {
        label: 'Polgo - Aprovar envio de resgate de pontos',
        description: 'Autoriza aprovação de resgates de pontos.',
      },
    ],
  },
  {
    id: 'banco',
    title: 'Banco de dados',
    icon: 'fas fa-database',
    items: [
      {
        label: 'Limpar banco de teste',
        description: 'Operação destrutiva em ambiente de teste.',
      },
      {
        label: 'Limpar banco de produção',
        description: 'Operação destrutiva em ambiente de produção.',
      },
    ],
  },
  {
    id: 'dump-backup',
    title: 'Dump e backup',
    icon: 'fas fa-archive',
    items: [
      {
        label: 'Dump - Gerar dump prod',
        description: 'Gera dump do banco de produção.',
      },
      {
        label: 'Dump - Gerar dump teste',
        description: 'Gera dump do banco de teste.',
      },
      {
        label: 'Backup - Restaurar backup prod',
        description: 'Restaura backup em produção.',
      },
      {
        label: 'Backup - Restaurar backup teste',
        description: 'Restaura backup em teste.',
      },
    ],
  },
];

export const PERMISSOES_2FA_AUTOCOMPLETE = PERMISSOES_2FA_GROUPS.flatMap((group) =>
  group.items.map((item) => item.label),
);

export function createEmptyAppPermission() {
  return { acesso: false, roles: [] };
}

export function parseUserAppPermission(entry) {
  if (Array.isArray(entry)) {
    const roles = entry.filter(Boolean);
    return {
      acesso: roles.length > 0,
      roles: [...new Set(roles)],
    };
  }

  if (entry && typeof entry === 'object') {
    const roles = Array.isArray(entry.roles)
      ? entry.roles.filter(Boolean)
      : [];

    return {
      acesso: entry.acesso === true,
      roles: [...new Set(roles)],
    };
  }

  return createEmptyAppPermission();
}

export function normalizeUserAppsForForm(apps) {
  const result = {};

  for (const [appId, entry] of Object.entries(apps || {})) {
    result[appId] = parseUserAppPermission(entry);
  }

  return result;
}

export function normalizeUserAppsForApi(apps) {
  const result = {};

  for (const [appId, entry] of Object.entries(apps || {})) {
    const parsed = parseUserAppPermission(entry);
    if (!parsed.acesso && parsed.roles.length === 0) {
      continue;
    }
    result[appId] = parsed;
  }

  return result;
}

export function hasAppAccess(apps, appId) {
  return parseUserAppPermission(apps?.[appId]).acesso;
}

export function hasAppRole(apps, appId, role) {
  const permission = parseUserAppPermission(apps?.[appId]);
  return permission.roles.includes(role);
}

export function toggleAppAccess(apps, appId, checked) {
  const nextApps = { ...(apps || {}) };
  const current = parseUserAppPermission(nextApps[appId]);

  if (!checked && current.roles.length === 0) {
    delete nextApps[appId];
    return nextApps;
  }

  nextApps[appId] = {
    acesso: checked,
    roles: current.roles,
  };

  return nextApps;
}

export function toggleAppRole(apps, appId, role, checked) {
  const nextApps = { ...(apps || {}) };
  const current = parseUserAppPermission(nextApps[appId]);

  let roles = [...current.roles];
  let acesso = current.acesso;

  if (checked) {
    roles = [...new Set([...roles, role])];
    acesso = true;
  } else {
    roles = roles.filter((item) => item !== role);
  }

  if (!acesso && roles.length === 0) {
    delete nextApps[appId];
    return nextApps;
  }

  nextApps[appId] = { acesso, roles };
  return nextApps;
}

export function getAppRolesForUser(apps, appId) {
  return parseUserAppPermission(apps?.[appId]).roles;
}

export function countAppRolesForUser(apps, appId) {
  return getAppRolesForUser(apps, appId).length;
}

const MAP_2FA_LABEL_TO_API = {
  'Polgo - Entregar prêmio': 'polgo_entregar_premio',
  'Polgo - Entregar prêmio de cupons sorteados': 'polgo_entregar_premio_cupom',
  'Polgo - Alterar tabela premiação': 'polgo_alterar_tabela_premiacao',
  'Polgo - Aprovar envio de resgate de pontos': 'polgo_aprovar_solicitacao_resgate',
  'Configurador - Gerenciar usuários': 'gerenciar_logins_configurador',
  'Configurador - Alterar configurações críticas': 'alterar_configuracoes_criticas_configurador',
  'Apps OAuth - Gerenciar aplicações': 'gerenciar_apps_oauth',
  'Limpar banco de teste': 'limpar_banco_teste',
  'Limpar banco de produção': 'limpar_banco_prod',
  'Dump - Gerar dump prod': 'gerar_dump_prod',
  'Dump - Gerar dump teste': 'gerar_dump_teste',
  'Backup - Restaurar backup prod': 'restore_prod',
  'Backup - Restaurar backup teste': 'restore_teste',
};

const MAP_2FA_API_TO_LABEL = Object.fromEntries(
  Object.entries(MAP_2FA_LABEL_TO_API).map(([label, api]) => [api, label]),
);
MAP_2FA_API_TO_LABEL.login_configurador = 'Configurador - Gerenciar usuários';

export function createEmptyUserForm() {
  return {
    id: '',
    usuario: '',
    descricao: '',
    senha: '',
    confirmarSenha: '',
    supervisores: [],
    onboarding: null,
    permissoes: {
      logins: '',
      clientes: '',
      templates: '',
      configuracoes: [],
      autenticacao2FA: [],
      apps: {},
    },
  };
}

export function createEmptyConviteForm() {
  return {
    usuario: '',
    descricao: '',
    supervisores: [],
  };
}

export function isOnboardingPendente(user) {
  return user?.onboarding?.status === 'pendente';
}

export function buildConvitePayload(form) {
  return {
    usuario: form.usuario,
    descricao: form.descricao,
    supervisores: Array.isArray(form.supervisores) ? form.supervisores : [],
  };
}

export function userToForm(user) {
  return {
    id: user.id,
    usuario: user.usuario || '',
    descricao: user.descricao || '',
    senha: '',
    confirmarSenha: '',
    supervisores: Array.isArray(user.supervisores) ? [...user.supervisores] : [],
    onboarding: user.onboarding || null,
    permissoes: {
      logins: user.permissoes?.logins || '',
      clientes: user.permissoes?.clientes || '',
      templates: user.permissoes?.templates || '',
      configuracoes: [...(user.permissoes?.configuracoes || [])],
      autenticacao2FA: format2FAForDisplay(user.permissoes?.autenticacao2FA),
      apps: normalizeUserAppsForForm(user.permissoes?.apps || {}),
    },
  };
}

export function format2FAForDisplay(permissoes) {
  if (!Array.isArray(permissoes) || !permissoes.length) {
    return [];
  }

  return permissoes
    .map((item) => MAP_2FA_API_TO_LABEL[item] || null)
    .filter(Boolean);
}

export function format2FAForApi(permissoes2FA, editando = false) {
  const mapped = permissoes2FA
    .map((item) => MAP_2FA_LABEL_TO_API[item])
    .filter(Boolean);

  if (editando && !mapped.includes('gerenciar_logins_configurador')) {
    mapped.push('gerenciar_logins_configurador');
  }

  return mapped;
}

export function buildUserPayload(form, editando = false) {
  const payload = {
    usuario: form.usuario,
    descricao: form.descricao,
    permissoes: buildPermissoesPayload(form.permissoes, editando),
    supervisores: Array.isArray(form.supervisores) ? form.supervisores : [],
  };

  if (form.id) {
    payload.id = form.id;
  }

  if (form.senha && form.senha.trim()) {
    payload.senha = form.senha;
  }

  return payload;
}

export function buildPermissoesPayload(formPermissoes, editando = false) {
  return {
    configuracoes: formPermissoes.configuracoes || [],
    logins: formPermissoes.logins || '',
    clientes: formPermissoes.clientes || '',
    templates: formPermissoes.templates || '',
    autenticacao2FA: format2FAForApi(formPermissoes.autenticacao2FA, editando),
    apps: normalizeUserAppsForApi(formPermissoes.apps),
  };
}

export function getPasswordStrengthChecks(value = '') {
  const text = String(value || '');

  return {
    minLength: text.length >= 8,
    lowercase: /[a-z]/.test(text),
    uppercase: /[A-Z]/.test(text),
    number: /\d/.test(text),
    symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(text),
  };
}

export function isStrongPassword(value, optional = false) {
  if (!value) {
    return optional;
  }

  const checks = getPasswordStrengthChecks(value);
  return Object.values(checks).every(Boolean);
}

export function getConfigPrivilege(configuracoes, aplicacao, nome) {
  const config = (configuracoes || []).find(
    (item) => item.aplicacao === aplicacao && item.nome === nome,
  );
  return config ? config.privilegio : '';
}

export function setConfigPrivilege(configuracoes, aplicacao, nome, privilege) {
  const list = [...(configuracoes || [])];

  if (privilege === '') {
    return list.filter((item) => !(item.aplicacao === aplicacao && item.nome === nome));
  }

  const index = list.findIndex((item) => item.aplicacao === aplicacao && item.nome === nome);
  if (index !== -1) {
    list[index] = { ...list[index], privilegio: privilege };
    return list;
  }

  return [...list, { aplicacao, nome, privilegio: privilege }];
}

export function setAllConfigPrivileges(templates, privilege) {
  if (privilege === '') {
    return [];
  }
  return templates.map((template) => ({
    aplicacao: template.aplicacao,
    nome: template.nome,
    privilegio: privilege,
  }));
}

export function is2FAPermissionSelected(selectedLabels, label) {
  return (selectedLabels || []).includes(label);
}

export function toggle2FAPermission(selectedLabels, label, checked) {
  const current = [...(selectedLabels || [])];

  if (checked) {
    if (current.includes(label)) {
      return current;
    }
    return [...current, label];
  }

  return current.filter((item) => item !== label);
}

export function setGroup2FAPermissions(selectedLabels, groupItems, checked) {
  const labels = groupItems.map((item) => item.label);
  const current = [...(selectedLabels || [])].filter((item) => !labels.includes(item));

  if (!checked) {
    return current;
  }

  return [...new Set([...current, ...labels])];
}

export function count2FASelected(selectedLabels) {
  return (selectedLabels || []).length;
}
